<script lang="ts">
	import type { Issue } from '#lib/types';
	import { i18n, t } from '#lib/i18n/state.svelte';
	import { issueTextEn } from '#lib/i18n/issues';
	import { SEVERITY_UI, severityLabel, confidenceLabel, CONFIDENCE_UI } from '#lib/ui/labels';

	interface Props {
		issue: Issue;
		expanded?: boolean;
		onjump?: (issue: Issue) => void;
		ontoggle?: (issue: Issue) => void;
	}

	let { issue, expanded = false, onjump, ontoggle }: Props = $props();

	const sev = $derived(SEVERITY_UI[issue.severity]);
	const sevText = $derived(severityLabel(issue.severity));
	const conf = $derived(CONFIDENCE_UI[issue.confidence]);
	const confText = $derived(confidenceLabel(issue.confidence));
	const localized = $derived(i18n.lang === 'en' ? issueTextEn(issue) : null);
	const title = $derived(localized?.title ?? issue.title);
	const detail = $derived(localized?.detail ?? issue.detail);
	const suggestion = $derived(localized?.suggestion ?? issue.suggestion);
	const pageLabel = $derived(
		issue.location.page !== undefined
			? t('common.page', { n: issue.location.page })
			: issue.location.sourceLine !== undefined
				? t('common.line', { n: issue.location.sourceLine })
				: ''
	);
</script>

<article class="issue {expanded ? 'is-open' : ''}" style:--sev={sev.dot} id="issue-{issue.id}">
	<button class="head" onclick={() => ontoggle?.(issue)} aria-expanded={expanded}>
		<span class="sev-dot" aria-hidden="true">{sev.icon}</span>
		<span class="titles">
			<span class="title">{title}</span>
			<span class="sub">
				<span class="chip {sev.chip}">{sevText}</span>
				{#if !issue.decisive}
					<span class="chip chip-neutral" title={t('issue.confidenceTitle', { level: confText })}>
						{t('issue.notDecisive')}
					</span>
				{/if}
				{#if pageLabel}
					<span class="loc">{pageLabel}</span>
				{/if}
			</span>
		</span>
		<span class="chev" aria-hidden="true">{expanded ? '−' : '+'}</span>
	</button>

	{#if expanded}
		<div class="body">
			<p class="detail">{detail}</p>

			{#if issue.quote}
				<blockquote class="quote">
					{issue.quote}
				</blockquote>
			{/if}

			{#if suggestion}
				<p class="suggestion"><span class="lbl">{t('issue.suggestion')}</span> {suggestion}</p>
			{/if}

			<div class="foot">
				<span class="conf" style:color={conf.tone} title={t('issue.confidenceTitle', { level: confText })}>
					<span class="conf-bar" aria-hidden="true">
						<span style:width="{issue.confidence === 'high' ? 100 : issue.confidence === 'medium' ? 60 : 28}%"
						></span>
					</span>
					{confText}
				</span>
				<span class="code mono">{issue.code}</span>
				{#if onjump && issue.location.charStart !== undefined}
					<button class="btn btn-sm jump" onclick={() => onjump(issue)}>{t('issue.showInText')}</button>
				{/if}
			</div>
		</div>
	{/if}
</article>

<style>
	.issue {
		border: 1px solid var(--line);
		border-left: 3px solid var(--sev);
		border-radius: 10px;
		background: var(--card);
		overflow: hidden;
		transition:
			box-shadow 0.18s var(--ease),
			border-color 0.18s var(--ease);
	}
	.issue:hover {
		box-shadow: var(--shadow-sm);
	}
	.is-open {
		box-shadow: var(--shadow-sm);
	}
	.head {
		display: flex;
		align-items: flex-start;
		gap: 0.7rem;
		width: 100%;
		padding: 0.8rem 0.9rem;
		background: transparent;
		border: 0;
		cursor: pointer;
		text-align: left;
	}
	.head:focus-visible {
		outline: 2px solid var(--accent);
		outline-offset: -2px;
	}
	.sev-dot {
		width: 20px;
		height: 20px;
		flex: none;
		border-radius: 50%;
		display: grid;
		place-items: center;
		font-size: 11px;
		font-weight: 800;
		color: #fff;
		background: var(--sev);
		margin-top: 1px;
	}
	.titles {
		display: flex;
		flex-direction: column;
		gap: 0.3rem;
		min-width: 0;
		flex: 1;
	}
	.title {
		font-weight: 550;
		font-size: 0.93rem;
		line-height: 1.35;
	}
	.sub {
		display: flex;
		align-items: center;
		gap: 0.4rem;
		flex-wrap: wrap;
	}
	.loc {
		font-size: 0.72rem;
		color: var(--muted);
	}
	.chev {
		font-size: 1.1rem;
		color: var(--muted);
		line-height: 1;
		margin-top: 2px;
	}
	.body {
		padding: 0 0.9rem 0.9rem 2.5rem;
		animation: fade-up 0.25s var(--ease) both;
	}
	.detail {
		margin: 0 0 0.6rem;
		font-size: 0.88rem;
		color: var(--ink-2);
		line-height: 1.55;
	}
	.quote {
		margin: 0 0 0.6rem;
		padding: 0.55rem 0.75rem;
		background: var(--paper-2);
		border-left: 2px solid var(--line-strong);
		border-radius: 0 6px 6px 0;
		font-size: 0.82rem;
		color: var(--ink-2);
		line-height: 1.5;
		font-style: italic;
	}
	.suggestion {
		margin: 0 0 0.7rem;
		font-size: 0.84rem;
		color: var(--ink-2);
	}
	.lbl {
		display: inline-block;
		font-size: 0.68rem;
		font-weight: 700;
		letter-spacing: 0.05em;
		text-transform: uppercase;
		color: var(--accent-ink);
		margin-right: 0.35rem;
	}
	.foot {
		display: flex;
		align-items: center;
		gap: 0.7rem;
		flex-wrap: wrap;
		padding-top: 0.55rem;
		border-top: 1px solid var(--line);
	}
	.conf {
		display: inline-flex;
		align-items: center;
		gap: 0.4rem;
		font-size: 0.72rem;
		font-weight: 600;
	}
	.conf-bar {
		width: 26px;
		height: 4px;
		border-radius: 99px;
		background: var(--line);
		overflow: hidden;
	}
	.conf-bar span {
		display: block;
		height: 100%;
		background: currentColor;
		border-radius: inherit;
	}
	.code {
		color: var(--muted);
		font-size: 0.7rem;
	}
	.jump {
		margin-left: auto;
	}
</style>
